import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { isoDate } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../constants";
import { RoadSensePayload } from "../plugins/roadsense/payload";
import { ItemByIdPath } from "./get-report";

/**
 * One RoadSense site's render payload, computed live from the report's stored
 * `job.json` plus a fresh read of the traffic-flow buckets, with an open
 * lifecycle's window extended to the site-local today.
 */
export const LivePayloadPath = ItemByIdPath.extend({
  // A plain string so an unknown site is a 404 from the handler, not a 400.
  siteId: z.string().min(1),
});
export type LivePayloadPath = z.output<typeof LivePayloadPath>;

export const RoadSenseLivePayloadResponse = z.object({
  data: z.object({
    payload: RoadSensePayload,
    /** ISO-8601 UTC instant the payload was computed at. */
    asOf: z.iso.datetime(),
    /** This site's `.xlsx` key from the row's listing summary, when the report has one. */
    workbookS3Key: z.string().optional(),
    /** The last local day the stored `.xlsx` covers, when it is known (see the handler). */
    workbookPeriodEnd: isoDate.optional(),
  }),
});
export type RoadSenseLivePayloadResponse = z.output<typeof RoadSenseLivePayloadResponse>;

export const roadsenseLivePayloadResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/items/by-id/{reportId}/sites/{siteId}/live",
  resourcePathSchema: LivePayloadPath,
  scope: "tenant",
});

export const getRoadsenseLivePayloadEndpoint = defineEndpoint(roadsenseLivePayloadResource, {
  responseSchema: RoadSenseLivePayloadResponse,
});
